import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import type { ComponentProps } from 'react';

import type { Score } from '#src/components/scoring/Score/index.tsx';
import { ScoreByMode } from '#src/components/scoring/ScoreByMode/index.tsx';
import type { TotalAveragePlayerScore } from '#src/lib/averagePlayerScore.ts';
import { averagePlayerScoresFragment } from '#src/lib/players.ts';

import type { PlayerScore_anyPlayer } from './__generated__/index.graphql.ts';

type Props = {
  player: PlayerScore_anyPlayer;
  mode: TotalAveragePlayerScore;
  remainingPoints?: number;
  hideTooltip?: boolean;
  small?: boolean;
} & Pick<
  ComponentProps<typeof Score>,
  'animated' | 'isCappedMode' | 'shape' | 'size'
>;

export const PlayerScore = ({
  player,
  mode,
  isCappedMode,
  remainingPoints,
  animated,
  hideTooltip,
  shape = 'HEXAGON',
  small,
}: Props) => {
  const scores = {
    LAST_FORTY_SO5_AVERAGE_SCORE: player.lastFortySo5AverageScore,
    LAST_TEN_PLAYED_SO5_AVERAGE_SCORE: player.lastTenPlayedSo5AverageScore,
    LAST_FIVE_SO5_AVERAGE_SCORE: player.lastFiveSo5AverageScore,
    LAST_FIFTEEN_SO5_AVERAGE_SCORE: player.lastFifteenSo5AverageScore,
    SEASON_AVERAGE_SCORE: player.seasonAverage,
  };

  return (
    <ScoreByMode
      hideTooltip={hideTooltip}
      shape={shape}
      scores={scores}
      mode={mode}
      isCappedMode={isCappedMode}
      animated={animated}
      remainingPoints={remainingPoints}
      size={small ? 3 : undefined}
    />
  );
};

PlayerScore.fragments = {
  anyPlayer: gql`
    fragment PlayerScore_anyPlayer on AnyPlayerInterface {
      slug
      ...AverageScores_anyPlayer
    }
    ${averagePlayerScoresFragment}
  ` as TypedDocumentNode<PlayerScore_anyPlayer>,
};
